
import React from 'react';
import { useAuth } from '../AuthContext';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';

export default function Footer() {
  const { user } = useAuth();
  const { t } = useLanguage();

  const getRoleAccent = () => {
    if (user?.role === 'admin') return 'linear-gradient(135deg, #6366f1, #8b5cf6)';
    if (user?.role === 'professional') return 'linear-gradient(135deg, #10b981, #059669)';
    if (user?.role === 'client') return 'linear-gradient(135deg, #f59e0b, #ec4899)';
    return 'var(--gradient-primary)';
  };

  return (
    <footer style={{
      position: 'relative',
      marginTop: '80px',
      overflow: 'visible',
    }}>
      {/* Wave separator */}
      <div style={{
        position: 'absolute',
        top: '-28px',
        left: 0,
        right: 0,
        height: '60px',
        overflow: 'hidden',
        pointerEvents: 'none',
      }}>
        <svg viewBox="0 0 1200 60" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
          <path
            d="M0,30 C200,60 400,0 600,30 C800,60 1000,0 1200,30 L1200,60 L0,60 Z"
            fill="var(--bg-elevated)"
          />
        </svg>
      </div>

      <div style={{
        background: 'var(--bg-elevated)',
        borderTop: '1px solid var(--border-subtle)',
        padding: '72px 24px 24px',
      }}>
        <div style={{
          maxWidth: '1280px',
          margin: '0 auto',
        }}>
          {/* Footer content grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '32px',
            marginBottom: '32px',
          }}>
            {/* Brand */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '12px',
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: getRoleAccent(),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'var(--font-display)',
                  fontWeight: 800,
                  fontSize: '0.9rem',
                  color: '#fff',
                }}>D</div>
                <span style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  background: getRoleAccent(),
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>DevGenius</span>
              </div>
              <p style={{
                fontSize: '0.85rem',
                color: 'var(--text-muted)',
                lineHeight: 1.6,
                maxWidth: '280px',
              }}>
                {t('footerTagline')}
              </p>
            </motion.div>

            {/* Links */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <h4 style={{
                fontFamily: 'var(--font-display)',
                fontSize: '0.85rem',
                fontWeight: 600,
                color: 'var(--text-secondary)',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                marginBottom: '16px',
              }}>{t('footerPlatform')}</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {[t('footerBrowseProfessionals'), t('footerExploreProjects'), t('footerPostProject')].map(item => (
                  <span key={item} style={{
                    fontSize: '0.85rem',
                    color: 'var(--text-muted)',
                    cursor: 'pointer',
                    transition: 'color 0.3s',
                  }}
                  onMouseEnter={e => e.target.style.color = 'var(--accent-cyan)'}
                  onMouseLeave={e => e.target.style.color = 'var(--text-muted)'}
                  >{item}</span>
                ))}
              </div>
            </motion.div>

            {/* Status */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <h4 style={{
                fontFamily: 'var(--font-display)',
                fontSize: '0.85rem',
                fontWeight: 600,
                color: 'var(--text-secondary)',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                marginBottom: '16px',
              }}>{t('footerStatus')}</h4>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: '#10b981',
                  boxShadow: '0 0 8px rgba(16, 185, 129, 0.5)',
                  animation: 'pulse-glow 2s ease-in-out infinite',
                }} />
                <span style={{ fontSize: '0.85rem', color: 'var(--accent-emerald)' }}>
                  {t('footerDemoModeActive')}
                </span>
              </div>
            </motion.div>
          </div>

          {/* Bottom divider + copyright */}
          <div style={{
            height: '1px',
            background: 'linear-gradient(90deg, transparent, var(--border-glass), transparent)',
            marginBottom: '20px',
          }} />
          
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '12px',
          }}>
            <p style={{
              fontSize: '0.8rem',
              color: 'var(--text-muted)',
              margin: 0,
            }}>
              &copy; {new Date().getFullYear()} DevGenius. {t('footerRights')}
            </p>
            <div style={{
              display: 'flex',
              gap: '16px',
              alignItems: 'center',
            }}>
              {[t('footerPrivacy'), t('footerTerms'), t('footerContact')].map(item => (
                <span key={item} style={{
                  fontSize: '0.8rem',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  transition: 'color 0.3s',
                }}
                onMouseEnter={e => e.target.style.color = 'var(--accent-cyan)'}
                onMouseLeave={e => e.target.style.color = 'var(--text-muted)'}
                >{item}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
  
  